<template>
  <ValidationObserver ref="proposalFormInitPage">
    <div class="card card-custom">
      <div class="card-body container">

        <appetite :brand="brand"/>

        <b-card border-variant="primary" class="mb-5 mt-3" body-class="px-7 py-5">
          <b-alert v-if="errorAlert" variant="warning" show>
            <div class="text-dark" v-html="errorAlert"></div>
          </b-alert>

          <div v-if="brand !== 'FUSE'" class="form-group row">
            <div class="col-6">
              <span class="font-weight-bold font-size-h4">
                Is the person initiating this proposal form a Broker?<span class="text-danger">*</span>
              </span>
              <b-form-group class="mt-2 mb-0">
                <ValidationProvider rules="required" name="personCompletingForm" v-slot="{ errors }">
                  <b-form-radio-group type="radio" v-model="userInfo.personCompletingForm">
                    <b-form-radio type="radio" value="broker">Yes</b-form-radio>
                    <b-form-radio type="radio" value="client">No</b-form-radio>
                  </b-form-radio-group>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </b-form-group>
            </div>
          </div>

          <div v-if="brand === 'RENTSURE' && userInfo.personCompletingForm === 'broker'">
            <strong>
              You are requested to complete the <a href="https://crinsurance.com.au">Car Rental Insurance</a> Proposal Form. Click on the button below to access the Proposal Form.<br/><br/>
              <a class="btn btn-primary" target="_blank" href="https://link.crinsurance.com.au/start-proposal-form">Car Rental Insurance Proposal Form</a>
            </strong>
          </div>

          <div v-if="brand === 'CRI' && userInfo.personCompletingForm === 'client'">
            <strong>
              You are requested to complete the <a href="https://rentsure.com.au">Rentsure</a> Proposal Form. Click on the button below to access the Proposal Form.<br/><br/>
              <a class="btn btn-primary" target="_blank" href="https://link.rentsure.com.au/start-proposal-form">Rentsure Proposal Form</a>
            </strong>
          </div>

          <div v-if="isValidBrandForm">
            <div v-if="userInfo.personCompletingForm === 'broker'" class="form-group">
              <span class="font-weight-bolder">Tip:</span> As a broker, you can either complete the Proposal Form yourself or share the link with your customer to fill in directly.
              The form auto-saves as you go and stays editable until the final submission, so you or your customer can review and update it before submitting.
            </div>

            <span class="font-weight-bold font-size-lg">Please enter your details below to receive the proposal form link.</span>

            <div class="form-group row">
              <div class="col-4">
                <label class="col-form-label text-left">
                  First Name<span class="text-danger">*</span>
                </label>
                <ValidationProvider rules="required|max:255" name="firstName" v-slot="{ errors }">
                  <input type="text" class="form-control" v-model.trim="userInfo.firstName" required maxlength="255"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="col-4">
                <label class="col-form-label text-left">
                  Last Name<span class="text-danger">*</span>
                </label>
                <ValidationProvider rules="required|max:255" name="lastName" v-slot="{ errors }">
                  <input type="text" class="form-control" v-model.trim="userInfo.lastName" required maxlength="255"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="col-4">
                <label class="col-form-label text-left">
                  Email<span class="text-danger">*</span>
                </label>
                <ValidationProvider rules="required|email|max:100" name="email" v-slot="{ errors }">
                  <input type="text" class="form-control" v-model.trim="userInfo.email" required maxlength="100"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>

            <span class="font-weight-bold font-size-h5">Proposed Insured Entity</span>
            <div class="form-group row mb-2 mt-2">
              <label class="col-1 col-form-label">Search ABN<span class="text-danger">*</span></label>
              <span class="col-3">
                <ValidationProvider :rules="{ regex: /^[\d\s]+$/ }" name="abnSearchString" v-slot="{ errors }">
                  <input type="text" class="form-control" v-model.trim="abnSearchString" maxlength="15"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </span>
              <button type="button" class="col-1 btn btn-primary" @click.prevent="searchAbn"
                      :disabled="searchingAbn || !abnSearchString">Search</button>
              <b-spinner v-if="searchingAbn" variant="primary" class="ml-3 mt-1"></b-spinner>
            </div>
            <div class="form-group" v-if="abnSearchError">
              <label class="text-danger">{{ abnSearchError }}</label>
            </div>

            <div v-if="legalEntity && legalEntity.entityName && legalEntity.abn" class="mb-5 mt-5">
              <div class="form-group row mb-0">
                <div class="col-1 font-size-h6">Entity Name</div>
                <div class="col-5 font-weight-boldest font-size-h6">{{ legalEntity.entityName }}</div>
              </div>
              <div class="form-group row mb-0">
                <div class="col-1 font-size-h6">ABN</div>
                <div class="col-5 font-size-h6">{{ legalEntity.abn }}</div>
              </div>
              <div v-if="legalEntity.acn" class="form-group row mb-0">
                <div class="col-1 font-size-h6">ACN</div>
                <div class="col-5 font-size-h6">{{ legalEntity.acn }}</div>
              </div>

              <ValidationProvider :rules="{'required-action': {allowFalse:false}}" name="legalEntityConfirmed" v-slot="{ errors }">
                <div class="row mt-2">
                  <span class="switch switch-sm switch-outline switch-icon switch-primary ml-3">
                    <label>
                      <input type="checkbox" name="select" v-model="legalEntityConfirmed"/>
                      <span></span>
                    </label>
                  </span>
                  <label class="col-form-label ml-2 font-size-h6">
                    I confirm that the proposed insured entity displayed above is correct.
                  </label>
                </div>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <div v-if="legalEntityConfirmed" class="mt-10">
              Please complete the captcha below.
              <vue-recaptcha :sitekey="'6LfROSYfAAAAANtO-fIhOZURzKagc6XgofmbEPIt'"
                             @verify="captchaVerified=true" @expired="captchaVerified=false"
              >
              </vue-recaptcha>
              <div class="d-flex justify-content-between mt-10">
                <div class="mr-2">
                  <button class="btn btn-primary font-weight-bold text-uppercase px-9 py-4"
                          @click.prevent="submitForm" :disabled="!captchaVerified">
                    Submit
                  </button>
                </div>
              </div>
            </div>

          </div>
        </b-card>
      </div>
    </div>
  </ValidationObserver>
</template>

<style lang="scss">

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>

<script>
import {ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME} from "@/core/services/store/htmlclass.module";
import _ from "lodash";
import Swal from "sweetalert2";
import KTUtil from "@/assets/js/components/util";
import { VueRecaptcha } from 'vue-recaptcha';
import BrandService from "@/core/services/brand.service";
import Appetite from "@/view/pages/wizard/motor-proposal-form/components/Appetite.vue";
import ApiService from "@/core/services/api.service";

export default {
  name: "MotorProposalFormInit",

  components: {Appetite, VueRecaptcha },

  data() {
    return {
      brand: null,
      captchaVerified: false,
      errorAlert: null,

      abnSearchString: null,
      abnSearchError: null,
      searchingAbn: false,
      legalEntity: null,
      legalEntityConfirmed: false,

      userInfo: {
        personCompletingForm: null,
        firstName: null,
        lastName: null,
        email: null,
        abn: null
      }
    };
  },

  mounted() {
    this.startPageLoading();

    // Extract information from URL params
    const queryParams = this.$route.query;
    if (!queryParams.brand) {
      this.handleError("Unauthorised access!");
      return;
    }

    // Extract data from URL params
    this.brand = _.toUpper(queryParams.brand);
    this.userInfo.brand = this.brand;

    // Set Title and Brand
    this.$store.commit("setWizardTitle", "Motor Proposal Form");

    // Set UI Brand
    BrandService.setUIBrand(this.brand);

    this.stopPageLoading();
  },

  computed: {
    isValidBrandForm: function() {
      const pcf = this.userInfo.personCompletingForm;
      if(this.brand === 'RENTSURE') {
        return pcf === 'client';
      }
      else if(this.brand === 'CRI') {
        return pcf === 'broker';
      }
      else if(this.brand === 'FUSE') {
        return true;
      }
      return false;
    }
  },

  methods: {

    searchAbn: function() {
      if(!this.abnSearchString) {
        return;
      }

      this.legalEntityConfirmed = false;
      this.legalEntity = null;

      this.abnSearchError = null;
      this.searchingAbn = true;
      const searchString = this.abnSearchString.replace(/\s/g, '');
      ApiService.get(`/mpf/abn/search`, { params: {searchString: searchString} })
          .then(({ data }) => {
            const entity = data.result;

            if(!entity || !entity.abn || !entity.entityName) {
              this.abnSearchError = 'No entity found for the given ABN';
            }
            else if(entity.abnStatus !== 'Active') {
              this.abnSearchError = 'This ABN is not in active status';
            }
            else {
              this.legalEntity = {
                entityName: entity.entityName,
                abn: entity.abn,
                acn: entity.acn
              };
            }
            this.searchingAbn = false;
          })
          .catch((error) => {
            this.searchingAbn = false;
            this.abnSearchError = 'Error while searching ABN - please try again';
            if(process.env.NODE_ENV === 'development') {
              console.log(error);
            }
          });
    },

    submitForm: async function () {
      if(!this.isValidBrandForm) {
        return;
      }

      const isFormValid = await this.$refs.proposalFormInitPage.validate();
      if(!isFormValid) {
        return;
      }

      this.startPageLoading();

      this.userInfo.abn = this.legalEntity.abn;
      ApiService.post(`/mpf/init-form`, this.userInfo)
        .then(() => {
          this.stopPageLoading();

          Swal.fire({
            title: "Thank&nbsp;You",
            html: `<p>You will receive a link in your email to access the Motor Proposal Form.</p>
                   <br/><span class="font-weight-bold">Please close this browser window.</span>`,
            icon: "success",
            width: "40rem",
            allowOutsideClick: false,
            allowEnterKey: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            showCancelButton: false,
            showCloseButton: false
          });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
    },

    handleError: function(errorObj) {
      console.log(errorObj);
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj) {
        let responseStatus = _.get(errorObj, "response.status");
        let errorMessage = errorObj.message || '';
        if (responseStatus === 401 || errorMessage.includes("status code 401") ||
            responseStatus === 403 || errorMessage.includes("status code 403")
        ) {
          errorMsg = "This session is invalid or expired. Please close this window.";
        }
        else {
          let responseResult = _.get(errorObj, "response.data.result");
          if (_.isString(responseResult)) {
            let applicationErrorPrefix = "UI_ERROR:";

            if (responseResult.startsWith(applicationErrorPrefix)) {
              errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
            }
          }
        }
      }
      if (!errorMsg) {
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  },
};
</script>
